import React, { useRef } from "react";
import ColorButton from "../../common/ColorButton";
import TSTable from "../../common/TSTable";
import { DatePicker, Modal,  theme,  Tooltip, Dropdown } from "antd";
import { EyeOutlined, ExclamationCircleOutlined, PrinterOutlined, DeleteOutlined, PlusCircleFilled,} from "@ant-design/icons";
import { PRIMARY_COLOR, PRINT_COLOR } from "../../constants/color";
import ErrorMessages from "../../common/ErrorMessages";
import PurchasePaymentVoucherModal from "./PurchasePaymentVoucherModal";
import { URL_POND, URL_REPORT, URL_TRUCK } from "../../constants/urls";
import { DELETE } from "../../frameworks/HttpClient";
import { currencyFormat, dateToHumanString } from "../../frameworks/Util";
import ReportButton from "../common/ReportButton";
import dayjs from 'dayjs';

const PurchasePaidListView = React.forwardRef((props, ref) => {
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [openPurchasePaymentVoucherModal, setOpenPurchasePaymentVoucherModal] = React.useState(false);
  const [billDate , setBillDate] = React.useState({bill_date : dayjs().format('YYYY-MM-DD')});
  const [tableKey, setTableKey] = React.useState(0);
  const [target, setTarget] = React.useState(null);
  const [reportLoading, setReportLoading] = React.useState(false);
  const tableRef = useRef(null);
  const { token: { colorSuccess, colorError },} = theme.useToken();
  const { isDone } = props;

  // const mockData = [{ id: "0", drc: 0.0, real_price: 0.0 }];

  const refreshData = async () => {
    if (tableRef.current) {
      setErrorMessages(null);
      try {
        await tableRef.current.fetchWithoutResetPage();
      } catch (error) {
        setErrorMessages(error.errorMessages);
      }
    }
  };

  const handleEdit = (original) => {
    if (!original.id) return;    
    setTarget(original);
    setOpenPurchasePaymentVoucherModal(true);
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await DELETE(`${URL_POND.POND_PAYMENT_TRANSACTION}${id}/`);
      refreshData();
    } catch (error) {}
  };

  const confirm = (title, id) => {
    Modal.confirm({
      title: `ยกเลิกใบสำคัญจ่าย - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color: colorError }} />,
      content: "คุณต้องการยกเลิกใบสำคัญจ่ายนี้ใช่หรือไม่?",
      okText: "ใช่",
      cancelText: "ไม่ใช่",
      maskClosable: true,
      okButtonProps: { style: { backgroundColor: colorError } },
      onOk: async () => {
        await handleDelete(id);
      },
    });
  };

  const columns = [
    {
      title: "Action",
      key: "action",
      dataIndex: "",
      render: (value, original) =>
        isDone ? null : (
          <center>
            <Tooltip title={"พิมพ์ใบสำคัญจ่าย"}>
              <Dropdown
                trigger="click"
                placement="top"
                arrow
                menu={{items: [
                  { 
                    key: 'order', 
                    label: (
                      <ReportButton
                        url={`${URL_REPORT.RAW_LATEX_RECEIPT}${original.id}/`}
                        block type='text'
                        sentLoading={setReportLoading}>
                        ขนาด A4
                      </ReportButton>
                    ) 
                  },
                  { 
                    key: 'temp', 
                    label: (
                      <ReportButton
                        url={`${URL_REPORT.RAW_LATEX_RECEIPT}${original.id}/?type=form-paper`}
                        block type='text'
                        sentLoading={setReportLoading}>
                        กระดาษฟอร์ม
                      </ReportButton>
                    ) 
                  },
              ]}}>
                <ColorButton
                  override={PRINT_COLOR}
                  style={{ margin: 5 }}
                  type='primary'
                  icon={<PrinterOutlined />}
                  loading={reportLoading}/>
              </Dropdown>
            </Tooltip>
            <Tooltip title={"ลบรายการ"}>
              <ColorButton
                type={"primary"}
                override={colorError}
                icon={<DeleteOutlined />}
                onClick={() => confirm(original.document_no, original.id)}
                style={{ margin: 5 }}
              />
            </Tooltip>
            <Tooltip title={"ข้อมูลใบสำคัญจ่าย"}>
              <ColorButton
                type={"primary"}
                override={PRIMARY_COLOR}
                icon={<EyeOutlined />}
                style={{ margin: 5 }}
                onClick={() => handleEdit(original)}
              />
            </Tooltip>
          </center>
        ),
    },
    {
      title: "วันที่",
      key: "bill_date",
      dataIndex: "bill_date",
      render : (value) => <center>{value ? dateToHumanString(value) : '-'}</center>
    },
    {
      title: "เลขที่เอกสาร",
      key: "document_no",
      dataIndex : 'document_no',
      searcher : true,
    },
    {
      title: "ชื่อผู้ขาย",
      key: "supplier_name",
      dataIndex: "supplier_name",
      searcher : true,
    },
    {
      title: "เลขบัญชี",
      key: "bank_account_no",
      dataIndex: "bank_account_no",
      searcher : true,
    },
    {
      title: "บัญชีบริษัท",
      key: "bank_account_detail",
      dataIndex: "bank_account_detail",
      searcher : true,
    },
    {
      title: "ราคารับซื้อ",
      key: "total_price",
      dataIndex: "total_price",
      render: (value) => <center>{currencyFormat(value ? Number(value) : 0)}</center>
    },
    {
      title: "ภาษี",
      key: "vat_percent",
      dataIndex: "vat_percent",
    },
    {
      title: "ราคารวม",
      key: "total_price_with_vat",
      dataIndex: "total_price_with_vat",
      render: (value) => <center>{currencyFormat(value ? value : 0)}</center>
    },
  ];

  const resetFilterData = async() => {
    if(tableRef.current){
      resetFilters();
    }
  }

  const resetFilters = () => {
    setTableKey(tableKey => tableKey + 1);
  };

    React.useEffect(() => {
      resetFilterData();
    },[billDate])

    React.useEffect(() => {
      const interval = setInterval(() => {
        refreshData();
      }, 300000);

      return () => clearInterval(interval);
    }, []);

  return (
    <div>
      <ErrorMessages message={errorMessages} />
      วันที่ : 
      <DatePicker
        style={{ marginBottom : 10, marginLeft : 10, marginRight : 20 }}
        onCalendarChange={(dates, dateStrings) => setBillDate({ bill_date : dateStrings })}
        defaultValue={[dayjs(), dayjs()]}
      />
      <ColorButton
        type="primary"
        override={colorSuccess}
        style={{ marginBottom: 10, float: "right" }}
        icon={<PlusCircleFilled />}
        onClick={() => setOpenPurchasePaymentVoucherModal(true)}
      >
        เพิ่มใบสำคัญจ่าย
      </ColorButton>
      <TSTable
        key={tableKey}
        columns={columns}
        size="small"
        url={URL_TRUCK.PAYMENT_TRANSACTION}
        ref={tableRef}
        params={{...billDate}}
      />

      <PurchasePaymentVoucherModal
        open={openPurchasePaymentVoucherModal}
        onClose={() => {
          setOpenPurchasePaymentVoucherModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenPurchasePaymentVoucherModal(false);
          setTarget(null);
          refreshData();
        }}
        target={target}
      />
    </div>
  );
});
export default PurchasePaidListView;
